import { useState } from "react";
import { Alert, Button, Group, Paper, Stack, Text, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { catalogue } from "../catalogue/api";
import styles from "../catalogue/Catalogue.module.scss";

/**
 * Pixels & Analytics: the Meta Pixel the customer application reports to (`R-ANL-8`).
 *
 * Its own card and its own save, apart from the show settings, because it governs no show — it
 * says where ad conversions are reported. The value is still `metaPixelId` in `platform_setting`,
 * written through the same `PUT /api/settings`, which takes a partial body. Public by nature, so
 * nothing here is masked; empty turns it off, and the server refuses anything that is not digits.
 */
export function PixelSettings({
  metaPixelId,
  onSaved,
}: {
  metaPixelId: string;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [pixel, setPixel] = useState<string>(metaPixelId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  async function save() {
    setBusy(true);
    setError(undefined);
    setSaved(false);
    try {
      await catalogue.saveSettings({ metaPixelId: pixel.trim() });
      setSaved(true);
      await onSaved();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t("shows.unreachable"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="sm">
        <Text fw={600}>{t("settings.analyticsHeading")}</Text>
        <Group gap="md" align="flex-end" wrap="wrap">
          <TextInput
            label={t("settings.pixelLabel")}
            description={t("settings.pixelHelp")}
            value={pixel}
            inputMode="numeric"
            placeholder="123456789012345"
            onChange={(event) => setPixel(event.currentTarget.value)}
            disabled={busy}
          />
          <Button
            onClick={() => void save()}
            loading={busy}
            disabled={pixel.trim() === metaPixelId}
          >
            {t("settings.save")}
          </Button>
        </Group>
        {saved ? (
          <Text size="xs" c="green">
            {t("settings.saved")}
          </Text>
        ) : null}
        {error ? (
          <Alert color="red" variant="light" radius="md">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
